import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Spinner, CardBody, Badge } from "reactstrap";
import * as Icons from "react-feather";
import Axios from "axios";
import moment from "moment";
import "moment/locale/id";
import Select from "react-select";
import { urlAPI, getToken, getUser } from "utility/Common";
import PerbandinganPage from "./PerbandinganPage";

moment.locale("id");

const jenisObatMap = {
  1: { name: "Obat", icon: "Package", color: "#3B82F6" },
  2: { name: "Alkes", icon: "Activity", color: "#10B981" },
  3: { name: "BHP", icon: "Droplet", color: "#F59E0B" },
};

const sbuMap = {
  RSPG: {
    name: "RSPG",
    fullName: "Rumah Sakit Petrokimia Gresik",
    color: "#3B82F6",
  },
  GRAHU: {
    name: "GRAHU",
    fullName: "Rumah Sakit Graha Husada",
    color: "#8B5CF6",
  },
  DRIYO: {
    name: "DRIYO",
    fullName: "Rumah Sakit Petrokimia Gresik Driyorejo",
    color: "#06B6D4",
  },
  "GUDANG BOD": {
    name: "Gudang BOD",
    fullName: "Gudang BOD",
    color: "#F59E0B",
  },
  HO: { name: "HO", fullName: "Head Office", color: "#6B7280" },
};

export default function DashboardPersediaan() {
  const userData = getUser();
  const userRole = userData?.role;
  const userSBU = userData?.sbu || "RSPG";

  const listSBU = [
    { value: "ALL", label: "Semua SBU" },
    { value: "RSPG", label: "Rumah Sakit Petrokimia Gresik" },
    { value: "GRAHU", label: "Rumah Sakit Graha Husada" },
    { value: "DRIYO", label: "Rumah Sakit Petrokimia Gresik Driyorejo" },
    { value: "GUDANG BOD", label: "Gudang BOD" },
    { value: "HO", label: "Head Office" },
  ];

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    pembelian: { total: 0, details: [] },
    persediaan: { total: 0, details: [], bySBU: {} },
    bahanMedis: { total: 0, details: [], bySBU: {} },
  });
  const [comparisonData, setComparisonData] = useState({
    current: { persediaan: { total: 0, details: [], bySBU: [] } },
    previous: { persediaan: { total: 0, details: [], bySBU: [] } },
  });
  const [activeSBU, setActiveSBU] = useState("ALL");
  const [filter, setFilter] = useState({
    bulan: moment().month() + 1,
    tahun: moment().year(),
  });
  const [previousFilter, setPreviousFilter] = useState({
    bulan: moment().subtract(1, "month").month() + 1,
    tahun: moment().subtract(1, "month").year(),
  });
  const [animateNumbers, setAnimateNumbers] = useState({});

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  const [showPerbandingan, setShowPerbandingan] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 992);
      setIsSmallMobile(window.innerWidth < 576);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const prev = moment(
      `${filter.tahun}-${String(filter.bulan).padStart(2, "0")}-01`,
    ).subtract(1, "month");
    setPreviousFilter({
      bulan: prev.month() + 1,
      tahun: prev.year(),
    });
  }, [filter.bulan, filter.tahun]);

  const fetchData = useCallback(
    (signal) => {
      setLoading(true);

      const params = JSON.stringify({
        bulan: filter.bulan,
        tahun: filter.tahun,
        previousBulan: previousFilter.bulan,
        previousTahun: previousFilter.tahun,
      });

      Axios.get(`${urlAPI}/v1/inventory/dashboardPersediaan`, {
        params: { params },
        headers: { Authorization: "Bearer " + getToken() },
        signal: signal,
      })
        .then((res) => {
          const data = res.data?.result?.rows || [];
          const previousData = res.data?.result?.previousRows || [];

          const pembelianDetails = [];
          let totalPembelian = 0;
          const persediaanBySBU = {};
          const persediaanDetails = [];
          let totalPersediaan = 0;
          const bahanMedisBySBU = {};
          const bahanMedisDetails = [];
          let totalBahanMedis = 0;

          data.forEach((item) => {
            const jenis = jenisObatMap[item.jenisobat];
            const jenisName = jenis?.name || `Jenis ${item.jenisobat}`;
            const jenisIcon = jenis?.icon || "Box";
            const jenisColor = jenis?.color || "#a18cd1";

            if (item.keterangan === "tb") {
              const value = parseFloat(item.beli) || 0;
              totalPembelian += value;
              pembelianDetails.push({
                jenis: jenisName,
                icon: jenisIcon,
                color: jenisColor,
                nilai: value,
              });
            } else if (item.keterangan === "saldo") {
              if (activeSBU !== "ALL" && item.sbu !== activeSBU) return;
              const value = parseFloat(item.beli) || 0;
              if (value === 0) return;

              const sbuInfo = sbuMap[item.sbu] || {
                name: item.sbu,
                fullName: item.sbu,
                color: "#a18cd1",
              };

              totalPersediaan += value;

              if (!persediaanBySBU[sbuInfo.name]) {
                persediaanBySBU[sbuInfo.name] = {
                  name: sbuInfo.name,
                  fullName: sbuInfo.fullName,
                  color: sbuInfo.color,
                  total: 0,
                  details: [],
                };
              }

              persediaanBySBU[sbuInfo.name].total += value;
              persediaanBySBU[sbuInfo.name].details.push({
                jenis: jenisName,
                icon: jenisIcon,
                color: jenisColor,
                nilai: value,
              });

              persediaanDetails.push({
                sbu: sbuInfo.name,
                sbuFull: sbuInfo.fullName,
                sbuColor: sbuInfo.color,
                jenis: jenisName,
                icon: jenisIcon,
                color: jenisColor,
                nilai: value,
              });
            } else if (item.keterangan === "bahan_medis") {
              if (activeSBU !== "ALL" && item.sbu !== activeSBU) return;
              const value = parseFloat(item.beli) || 0;
              if (value === 0) return;

              const sbuInfo = sbuMap[item.sbu] || {
                name: item.sbu,
                fullName: item.sbu,
                color: "#a18cd1",
              };

              totalBahanMedis += value;

              if (!bahanMedisBySBU[sbuInfo.name]) {
                bahanMedisBySBU[sbuInfo.name] = {
                  name: sbuInfo.name,
                  fullName: sbuInfo.fullName,
                  color: sbuInfo.color,
                  total: 0,
                  details: [],
                };
              }

              bahanMedisBySBU[sbuInfo.name].total += value;
              bahanMedisBySBU[sbuInfo.name].details.push({
                jenis: jenisName,
                icon: jenisIcon,
                color: jenisColor,
                nilai: value,
              });

              bahanMedisDetails.push({
                sbu: sbuInfo.name,
                sbuFull: sbuInfo.fullName,
                sbuColor: sbuInfo.color,
                jenis: jenisName,
                icon: jenisIcon,
                color: jenisColor,
                nilai: value,
              });
            }
          });

          pembelianDetails.forEach((item) => {
            item.persen =
              totalPembelian > 0 ? (item.nilai / totalPembelian) * 100 : 0;
          });

          const persediaanBySBUArray = Object.values(persediaanBySBU);
          const bahanMedisBySBUArray = Object.values(bahanMedisBySBU);

          setStats({
            pembelian: {
              total: totalPembelian,
              details: pembelianDetails,
            },
            persediaan: {
              total: totalPersediaan,
              details: persediaanDetails,
              bySBU: persediaanBySBUArray,
            },
            bahanMedis: {
              total: totalBahanMedis,
              details: bahanMedisDetails,
              bySBU: bahanMedisBySBUArray,
            },
          });

          // Proses previousData untuk persediaan periode sebelumnya
          const previousPersediaanBySBU = {};
          const previousPersediaanDetails = [];
          let totalPreviousPersediaan = 0;

          previousData.forEach((item) => {
            if (item.keterangan === "saldo_previous") {
              if (activeSBU !== "ALL" && item.sbu !== activeSBU) return;
              const value = parseFloat(item.beli) || 0;
              if (value === 0) return;

              const jenis = jenisObatMap[item.jenisobat];
              const jenisName = jenis?.name || `Jenis ${item.jenisobat}`;
              const jenisIcon = jenis?.icon || "Box";
              const jenisColor = jenis?.color || "#a18cd1";

              const sbuInfo = sbuMap[item.sbu] || {
                name: item.sbu,
                fullName: item.sbu,
                color: "#a18cd1",
              };

              totalPreviousPersediaan += value;

              if (!previousPersediaanBySBU[sbuInfo.name]) {
                previousPersediaanBySBU[sbuInfo.name] = {
                  name: sbuInfo.name,
                  fullName: sbuInfo.fullName,
                  color: sbuInfo.color,
                  total: 0,
                  details: [],
                };
              }

              previousPersediaanBySBU[sbuInfo.name].total += value;
              previousPersediaanBySBU[sbuInfo.name].details.push({
                jenis: jenisName,
                icon: jenisIcon,
                color: jenisColor,
                nilai: value,
              });

              previousPersediaanDetails.push({
                sbu: sbuInfo.name,
                sbuFull: sbuInfo.fullName,
                sbuColor: sbuInfo.color,
                jenis: jenisName,
                icon: jenisIcon,
                color: jenisColor,
                nilai: value,
              });
            }
          });

          setComparisonData({
            current: {
              persediaan: {
                total: totalPersediaan,
                details: persediaanDetails,
                bySBU: persediaanBySBUArray,
              },
            },
            previous: {
              persediaan: {
                total: parseFloat(totalPreviousPersediaan.toFixed(2)),
                details: previousPersediaanDetails,
                bySBU: Object.values(previousPersediaanBySBU),
              },
            },
          });

          setTimeout(() => {
            setAnimateNumbers({
              pembelian: totalPembelian,
              persediaan: totalPersediaan,
              bahanMedis: totalBahanMedis,
            });
          }, 100);

          setLoading(false);
        })
        .catch((err) => {
          if (!Axios.isCancel(err)) {
            console.error("Error fetching data:", err);
            setLoading(false);
          }
        });
    },
    [filter, previousFilter, activeSBU],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const formatNominal = (val) => {
    const number = Math.round(parseFloat(val) || 0);
    return `Rp ${number.toLocaleString("id-ID")}`;
  };

  const formatNumber = (val) => {
    const number = Math.round(parseFloat(val) || 0);
    return number.toLocaleString("id-ID");
  };

  const AnimatedNumber = ({ value }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
      let start = 0;
      const end = value;
      const duration = 1000;
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setDisplayValue(end);
          clearInterval(timer);
        } else {
          setDisplayValue(start);
        }
      }, 16);

      return () => clearInterval(timer);
    }, [value]);

    return (
      <span style={{ color: "white", fontSize: "20px", fontWeight: "bold" }}>
        {formatNumber(displayValue)}
      </span>
    );
  };

  const StatCard = ({ title, value, icon, subtitle, cardColor }) => {
    const defaultColor =
      cardColor || "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)";

    let animKey = "pembelian";
    if (title.toLowerCase().includes("persediaan")) animKey = "persediaan";
    if (title.toLowerCase().includes("bahan medis")) animKey = "bahanMedis";

    const isTotalNilai = title.includes("TOTAL NILAI");
    let mainTitle = title;
    let subTitleText = "";

    if (isTotalNilai) {
      const parts = title.split(" ");
      if (parts.length >= 3) {
        mainTitle = `${parts[0]} ${parts[1]}`;
        subTitleText = parts.slice(2).join(" ");
      }
    }

    return (
      <Card
        className="border-0 shadow-lg h-100"
        style={{
          borderRadius: "16px",
          background: defaultColor,
          transition: "all 0.3s ease",
          cursor: "pointer",
          overflow: "hidden",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-5px)";
          e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 10px 35px rgba(0,0,0,0.08)";
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            top: "-40px",
            right: "-40px",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
            bottom: "-20px",
            left: "-20px",
          }}
        />

        <CardBody className="p-3" style={{ position: "relative", zIndex: 1 }}>
          <div className="d-flex justify-content-between align-items-start">
            <div style={{ flex: 1 }}>
              <div className="mb-1">
                <p
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                    color: "rgba(255,255,255,0.95)",
                    textTransform: "uppercase",
                    marginBottom: "2px",
                  }}
                >
                  {mainTitle}
                </p>
                {subTitleText && (
                  <p
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "rgba(255,255,255,0.9)",
                      marginBottom: 0,
                      letterSpacing: "0.3px",
                    }}
                  >
                    {subTitleText}
                  </p>
                )}
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  marginBottom: "4px",
                  color: "white",
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  wordBreak: "break-word",
                  lineHeight: "1.2",
                }}
              >
                {animateNumbers[animKey] !== undefined ? (
                  <AnimatedNumber value={value} />
                ) : (
                  formatNumber(value)
                )}
              </div>
              {subtitle && (
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "rgba(255,255,255,0.9)",
                    display: "block",
                    marginTop: "2px",
                    lineHeight: "1.3",
                  }}
                >
                  {subtitle}
                </div>
              )}
            </div>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                color: "white",
                backdropFilter: "blur(10px)",
                flexShrink: 0,
                marginLeft: "10px",
              }}
            >
              {icon}
            </div>
          </div>
        </CardBody>
      </Card>
    );
  };

  const DonutChart = ({ data, total }) => {
    const canvasRef = React.useRef(null);
    const [canvasSize, setCanvasSize] = useState({ width: 180, height: 180 });

    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth < 576) {
          setCanvasSize({ width: 140, height: 140 });
        } else {
          setCanvasSize({ width: 180, height: 180 });
        }
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
      if (!canvasRef.current || data.length === 0) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const width = canvasSize.width;
      const height = canvasSize.height;
      canvas.width = width;
      canvas.height = height;

      const centerX = width / 2;
      const centerY = height / 2;
      const radius = width * 0.35;

      ctx.clearRect(0, 0, width, height);

      let startAngle = -Math.PI / 2;
      data.forEach((item) => {
        const angle = (item.nilai / total) * Math.PI * 2;
        const endAngle = startAngle + angle;

        ctx.beginPath();
        ctx.fillStyle = item.color;
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.fill();

        ctx.beginPath();
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.lineTo(centerX, centerY);
        ctx.stroke();

        startAngle = endAngle;
      });
    }, [data, total, canvasSize]);

    return (
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        style={{
          width: `${canvasSize.width}px`,
          height: `${canvasSize.height}px`,
          display: "block",
          margin: "0 auto",
        }}
      />
    );
  };

  const getPembelianBadgeText = () => {
    return "SEMUA SBU";
  };

  const getPersediaanBadgeText = (sbuName) => {
    const found = listSBU.find((s) => s.value === sbuName);
    if (found && found.value !== "ALL") return found.label;
    if (sbuName === "GUDANG BOD") return "Gudang BOD";
    if (sbuName === "HO") return "Head Office";
    if (sbuName === "RSPG") return "Rumah Sakit Petrokimia Gresik";
    if (sbuName === "GRAHU") return "Rumah Sakit Graha Husada";
    if (sbuName === "DRIYO") return "Rumah Sakit Petrokimia Gresik Driyorejo";
    return sbuName;
  };

  const getPersediaanSubtitle = () => {
    const currentMonth = moment().month() + 1;
    const currentYear = moment().year();
    const isCurrentMonth =
      filter.bulan === currentMonth && filter.tahun === currentYear;

    if (isCurrentMonth) {
      return "Persediaan Saat Ini";
    } else {
      return `Persediaan ${moment.months(filter.bulan - 1)} ${filter.tahun}`;
    }
  };

  const getPersediaanValue = () => {
    return stats.persediaan.total;
  };

  const getDisplayPersediaanData = () => {
    return stats.persediaan;
  };

  const sortSBU = (data) => {
    const order = ["RSPG", "GRAHU", "DRIYO", "GUDANG BOD", "HO"];
    if (!data || !data.bySBU) return [];

    return [...data.bySBU].sort((a, b) => {
      const indexA = order.indexOf(a.name);
      const indexB = order.indexOf(b.name);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  return (
    <div
      style={{
        background: "#F0F2F5",
        minHeight: "100vh",
        padding: "20px",
      }}
    >
      {showPerbandingan ? (
        <PerbandinganPage
          onBack={() => setShowPerbandingan(false)}
          currentPeriod={{
            bulan: filter.bulan,
            tahun: filter.tahun,
            label: `${moment.months(filter.bulan - 1)} ${filter.tahun}`,
          }}
          previousPeriod={{
            bulan: previousFilter.bulan,
            tahun: previousFilter.tahun,
            label: `${moment.months(previousFilter.bulan - 1)} ${previousFilter.tahun}`,
          }}
          currentData={comparisonData.current}
          previousData={comparisonData.previous}
        />
      ) : (
        <>
          <div className="mb-4">
            {/* BARIS 1: Judul - Kiri */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <div>
                <h1
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    marginBottom: "4px",
                    color: "#1a1a2e",
                  }}
                >
                  Dashboard Persediaan
                </h1>
                <p
                  className="mb-0"
                  style={{
                    fontSize: "13px",
                    color: "#000000",
                    fontWeight: "600",
                  }}
                >
                  Monitoring nilai pembelian, persediaan dan bahan medis
                </p>
              </div>
              {/* Spacer kosong agar judul tetap di kiri */}
              <div style={{ flex: 1 }} />
            </div>

            {/* BARIS 2: Controls - Kiri (Perbandingan), Tengah (SBU), Kanan (Tanggal) */}
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "8px" : "10px",
                alignItems: "center",
                justifyContent: isMobile ? "center" : "space-between",
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              {/* KIRI: Tombol Perbandingan Periode */}
              <button
                onClick={() => setShowPerbandingan(true)}
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  borderRadius: "10px",
                  padding: isMobile ? "8px 14px" : "8px 16px",
                  color: "white",
                  fontWeight: "600",
                  fontSize: isMobile ? "12px" : "13px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  whiteSpace: "nowrap",
                  width: isMobile ? "100%" : "auto",
                  minWidth: isMobile ? "unset" : "180px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0,0,0,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.15)";
                }}
              >
                <Icons.BarChart2 size={isMobile ? 14 : 16} />
                Perbandingan Periode
              </button>

              {/* TENGAH: Dropdown SBU */}
              <div
                style={{
                  width: isMobile ? "100%" : "240px",
                  minWidth: isMobile ? "unset" : "200px",
                  flex: isMobile ? "unset" : "0 1 auto",
                }}
              >
                <Select
                  options={listSBU}
                  value={listSBU.find((obj) => obj.value === activeSBU)}
                  onChange={(e) => setActiveSBU(e.value)}
                  placeholder="Pilih SBU..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "10px",
                      border: "1px solid #e0e0e0",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#667eea" },
                      minHeight: isMobile ? "38px" : "36px",
                      fontSize: isMobile ? "12px" : "13px",
                    }),
                  }}
                />
              </div>

              {/* KANAN: Filter Tanggal */}
              <div
                className="d-flex align-items-center bg-white shadow-sm"
                style={{
                  borderRadius: "10px",
                  padding: isMobile ? "6px 8px" : "6px 10px",
                  border: "1px solid #e0e0e0",
                  background: "white",
                  gap: isMobile ? "4px" : "6px",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  width: isMobile ? "100%" : "auto",
                  minWidth: isMobile ? "unset" : "200px",
                }}
              >
                <Icons.Calendar
                  size={isMobile ? 14 : 16}
                  className="text-primary"
                  style={{ flexShrink: 0 }}
                />

                <select
                  value={filter.bulan}
                  onChange={(e) =>
                    setFilter({ ...filter, bulan: parseInt(e.target.value) })
                  }
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    background: "white",
                    outline: "none",
                    fontSize: isMobile ? "11px" : "12px",
                    padding: isMobile ? "4px 4px" : "4px 6px",
                    cursor: "pointer",
                    flex: isMobile ? "1" : "unset",
                    minWidth: isMobile ? "60px" : "unset",
                  }}
                >
                  {moment.months().map((month, index) => (
                    <option key={index} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>

                <select
                  value={filter.tahun}
                  onChange={(e) =>
                    setFilter({ ...filter, tahun: parseInt(e.target.value) })
                  }
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    background: "white",
                    outline: "none",
                    fontSize: isMobile ? "11px" : "12px",
                    padding: isMobile ? "4px 4px" : "4px 6px",
                    cursor: "pointer",
                    flex: isMobile ? "1" : "unset",
                    minWidth: isMobile ? "60px" : "unset",
                  }}
                >
                  {[2022, 2023, 2024, 2025, 2026].map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>

                <Icons.RefreshCw
                  size={isMobile ? 12 : 14}
                  className="text-muted"
                  style={{
                    cursor: "pointer",
                    transition: "transform 0.3s",
                    flexShrink: 0,
                  }}
                  onClick={() => fetchData()}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "rotate(180deg)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "rotate(0)")
                  }
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div
              className="text-center py-5 d-flex align-items-center justify-content-center"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "16px",
                minHeight: "400px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  width: "200px",
                  height: "200px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.1)",
                  top: "-50px",
                  right: "-50px",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  width: "150px",
                  height: "150px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                  bottom: "-30px",
                  left: "-30px",
                }}
              />
              <div style={{ position: "relative", zIndex: 1 }}>
                <Spinner
                  color="light"
                  style={{ width: "3rem", height: "3rem", color: "white" }}
                >
                  Loading...
                </Spinner>
                <p
                  className="mt-3"
                  style={{
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Memuat data dashboard...
                </p>
              </div>
            </div>
          ) : (
            <>
              <Row className="mb-4 g-3">
                <Col xs={12} lg={4}>
                  <StatCard
                    title="TOTAL NILAI PEMBELIAN"
                    value={stats.pembelian.total}
                    icon={<Icons.ShoppingCart size={20} />}
                    subtitle={`${moment.months(filter.bulan - 1)} ${filter.tahun}`}
                    cardColor="linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)"
                  />
                </Col>
                <Col xs={12} lg={4}>
                  <StatCard
                    title="TOTAL NILAI PERSEDIAAN"
                    value={getPersediaanValue()}
                    icon={<Icons.Package size={20} />}
                    subtitle={getPersediaanSubtitle()}
                    cardColor="linear-gradient(135deg, #134e5e 0%, #1e7b6e 100%)"
                  />
                </Col>
                <Col xs={12} lg={4}>
                  <StatCard
                    title="TOTAL NILAI BIAYA BAHAN MEDIS"
                    value={stats.bahanMedis.total}
                    icon={<Icons.Activity size={20} />}
                    subtitle={`${moment.months(filter.bulan - 1)} ${filter.tahun}`}
                    cardColor="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                  />
                </Col>
              </Row>

              <Row className="mb-4 g-3">
                <Col xs={12}>
                  <Card
                    className="border-0 shadow-lg"
                    style={{
                      borderRadius: "16px",
                      height: "100%",
                      background: "white",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
                        padding: "14px 18px",
                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "6px",
                        }}
                      >
                        <h5
                          className="mb-0"
                          style={{
                            fontWeight: "bold",
                            color: "white",
                            fontSize: "16px",
                          }}
                        >
                          <Icons.PieChart
                            size={18}
                            className="me-2"
                            style={{ color: "white" }}
                          />
                          Komposisi Pembelian
                        </h5>
                        <Badge
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            color: "white",
                            padding: "4px 10px",
                            fontSize: "11px",
                          }}
                        >
                          {moment.months(filter.bulan - 1)} {filter.tahun}
                        </Badge>
                      </div>
                    </div>

                    <CardBody className="p-3">
                      {stats.pembelian.details.length > 0 ? (
                        <>
                          <div className="text-center mb-3">
                            <Badge
                              style={{
                                background: "#2a5298",
                                color: "white",
                                padding: "4px 12px",
                                fontSize: "12px",
                                fontWeight: "bold",
                                borderRadius: "20px",
                              }}
                            >
                              {getPembelianBadgeText()}
                            </Badge>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              gap: "20px",
                            }}
                          >
                            <div style={{ flex: "0 0 auto" }}>
                              <DonutChart
                                data={stats.pembelian.details}
                                total={stats.pembelian.total}
                              />
                            </div>
                            <div
                              style={{
                                flex: 1,
                                minWidth: "180px",
                              }}
                            >
                              {stats.pembelian.details.map((item, idx) => (
                                <div key={idx} className="mb-2">
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <div className="d-flex align-items-center">
                                      <div
                                        style={{
                                          width: "8px",
                                          height: "8px",
                                          borderRadius: "2px",
                                          background: item.color,
                                          marginRight: "6px",
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: 600,
                                          color: "#333",
                                        }}
                                      >
                                        {item.jenis}
                                      </span>
                                    </div>
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        color: item.color,
                                      }}
                                    >
                                      {formatNominal(item.nilai)}
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      style={{
                                        flex: 1,
                                        height: "4px",
                                        background: "#f0f0f0",
                                        borderRadius: "2px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: `${item.persen}%`,
                                          height: "100%",
                                          background: item.color,
                                          transition: "width 1s ease",
                                        }}
                                      />
                                    </div>
                                    <small
                                      style={{
                                        fontSize: "9px",
                                        fontWeight: 600,
                                        color: "#666",
                                        minWidth: "40px",
                                      }}
                                    >
                                      {item.persen.toFixed(1)}%
                                    </small>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div
                            className="mt-3 pt-3 text-center border-top"
                            style={{ borderTop: "1px solid #f0f0f0" }}
                          >
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#666",
                                textTransform: "uppercase",
                                letterSpacing: "1px",
                              }}
                            >
                              Total Nilai Pembelian
                            </span>
                            <div
                              style={{
                                fontSize: "18px",
                                fontWeight: "bold",
                                color: "#2a5298",
                                marginTop: "4px",
                              }}
                            >
                              {formatNominal(stats.pembelian.total)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-5 text-muted">
                          <Icons.Inbox size={48} className="mb-3" />
                          <p>Tidak ada data pembelian</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4 g-3">
                <Col xs={12} lg={6}>
                  <Card
                    className="border-0 shadow-lg"
                    style={{
                      borderRadius: "16px",
                      height: "100%",
                      background: "white",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #134e5e 0%, #1e7b6e 100%)",
                        padding: "14px 18px",
                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "6px",
                        }}
                      >
                        <h5
                          className="mb-0"
                          style={{
                            fontWeight: "bold",
                            color: "white",
                            fontSize: "16px",
                          }}
                        >
                          <Icons.PieChart
                            size={18}
                            className="me-2"
                            style={{ color: "white" }}
                          />
                          Persediaan
                        </h5>
                        <Badge
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            color: "white",
                            padding: "4px 10px",
                            fontSize: "11px",
                          }}
                        >
                          {moment.months(filter.bulan - 1)} {filter.tahun}
                        </Badge>
                      </div>
                    </div>

                    <CardBody className="p-3">
                      {(() => {
                        const displayData = getDisplayPersediaanData();
                        return displayData.details.length > 0 ? (
                          <>
                            {sortSBU(displayData).map(
                              (sbuData, index, array) => (
                                <div key={sbuData.name} className="mb-4">
                                  <div className="text-center mb-3">
                                    <Badge
                                      style={{
                                        background: "#1e7b6e",
                                        color: "white",
                                        padding: "4px 12px",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        borderRadius: "20px",
                                      }}
                                    >
                                      {getPersediaanBadgeText(sbuData.name)}
                                    </Badge>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      alignItems: "flex-start",
                                      justifyContent: "center",
                                      gap: "20px",
                                    }}
                                  >
                                    <div style={{ flex: "0 0 auto" }}>
                                      <DonutChart
                                        data={sbuData.details}
                                        total={sbuData.total}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        flex: 1,
                                        minWidth: "180px",
                                      }}
                                    >
                                      {sbuData.details.map((item, idx) => {
                                        const persen =
                                          (item.nilai / sbuData.total) * 100;
                                        return (
                                          <div key={idx} className="mb-2">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                              <div className="d-flex align-items-center">
                                                <div
                                                  style={{
                                                    width: "8px",
                                                    height: "8px",
                                                    borderRadius: "2px",
                                                    background: item.color,
                                                    marginRight: "6px",
                                                  }}
                                                />
                                                <span
                                                  style={{
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    color: "#333",
                                                  }}
                                                >
                                                  {item.jenis}
                                                </span>
                                              </div>
                                              <span
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "bold",
                                                  color: item.color,
                                                }}
                                              >
                                                {formatNominal(item.nilai)}
                                              </span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <div
                                                style={{
                                                  flex: 1,
                                                  height: "4px",
                                                  background: "#f0f0f0",
                                                  borderRadius: "2px",
                                                  overflow: "hidden",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    width: `${persen}%`,
                                                    height: "100%",
                                                    background: item.color,
                                                    transition: "width 1s ease",
                                                  }}
                                                />
                                              </div>
                                              <small
                                                style={{
                                                  fontSize: "9px",
                                                  fontWeight: 600,
                                                  color: "#666",
                                                  minWidth: "40px",
                                                }}
                                              >
                                                {persen.toFixed(1)}%
                                              </small>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  <div
                                    className="mt-3 pt-3 text-center border-top"
                                    style={{ borderTop: "1px solid #f0f0f0" }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#666",
                                        textTransform: "uppercase",
                                        letterSpacing: "1px",
                                      }}
                                    >
                                      Total Persediaan{" "}
                                      {getPersediaanBadgeText(sbuData.name)}
                                    </span>
                                    <div
                                      style={{
                                        fontSize: "18px",
                                        fontWeight: "bold",
                                        color: "#1e7b6e",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {formatNominal(sbuData.total)}
                                    </div>
                                  </div>
                                  {index !== array.length - 1 && (
                                    <hr
                                      style={{
                                        margin: "16px 0",
                                        borderColor: "#e0e0e0",
                                      }}
                                    />
                                  )}
                                </div>
                              ),
                            )}
                          </>
                        ) : (
                          <div className="text-center py-5 text-muted">
                            <Icons.Inbox size={48} className="mb-3" />
                            <p>Tidak ada data persediaan</p>
                          </div>
                        );
                      })()}
                    </CardBody>
                  </Card>
                </Col>

                <Col xs={12} lg={6}>
                  <Card
                    className="border-0 shadow-lg"
                    style={{
                      borderRadius: "16px",
                      height: "100%",
                      background: "white",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        padding: "14px 18px",
                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "6px",
                        }}
                      >
                        <h5
                          className="mb-0"
                          style={{
                            fontWeight: "bold",
                            color: "white",
                            fontSize: "16px",
                          }}
                        >
                          <Icons.PieChart
                            size={18}
                            className="me-2"
                            style={{ color: "white" }}
                          />
                          Biaya Bahan Medis
                        </h5>
                        <Badge
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            color: "white",
                            padding: "4px 10px",
                            fontSize: "11px",
                          }}
                        >
                          {moment.months(filter.bulan - 1)} {filter.tahun}
                        </Badge>
                      </div>
                    </div>

                    <CardBody className="p-3">
                      {stats.bahanMedis.details.length > 0 ? (
                        <>
                          {sortSBU(stats.bahanMedis).map(
                            (sbuData, index, array) => (
                              <div key={sbuData.name} className="mb-4">
                                <div className="text-center mb-3">
                                  <Badge
                                    style={{
                                      background: "#f5576c",
                                      color: "white",
                                      padding: "4px 12px",
                                      fontSize: "12px",
                                      fontWeight: "bold",
                                      borderRadius: "20px",
                                    }}
                                  >
                                    {getPersediaanBadgeText(sbuData.name)}
                                  </Badge>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    gap: "20px",
                                  }}
                                >
                                  <div style={{ flex: "0 0 auto" }}>
                                    <DonutChart
                                      data={sbuData.details}
                                      total={sbuData.total}
                                    />
                                  </div>
                                  <div
                                    style={{
                                      flex: 1,
                                      minWidth: "180px",
                                    }}
                                  >
                                    {sbuData.details.map((item, idx) => {
                                      const persen =
                                        (item.nilai / sbuData.total) * 100;
                                      return (
                                        <div key={idx} className="mb-2">
                                          <div className="d-flex justify-content-between align-items-center mb-1">
                                            <div className="d-flex align-items-center">
                                              <div
                                                style={{
                                                  width: "8px",
                                                  height: "8px",
                                                  borderRadius: "2px",
                                                  background: item.color,
                                                  marginRight: "6px",
                                                }}
                                              />
                                              <span
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: 600,
                                                  color: "#333",
                                                }}
                                              >
                                                {item.jenis}
                                              </span>
                                            </div>
                                            <span
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                                color: item.color,
                                              }}
                                            >
                                              {formatNominal(item.nilai)}
                                            </span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2">
                                            <div
                                              style={{
                                                flex: 1,
                                                height: "4px",
                                                background: "#f0f0f0",
                                                borderRadius: "2px",
                                                overflow: "hidden",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  width: `${persen}%`,
                                                  height: "100%",
                                                  background: item.color,
                                                  transition: "width 1s ease",
                                                }}
                                              />
                                            </div>
                                            <small
                                              style={{
                                                fontSize: "9px",
                                                fontWeight: 600,
                                                color: "#666",
                                                minWidth: "40px",
                                              }}
                                            >
                                              {persen.toFixed(1)}%
                                            </small>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div
                                  className="mt-3 pt-3 text-center border-top"
                                  style={{ borderTop: "1px solid #f0f0f0" }}
                                >
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      color: "#666",
                                      textTransform: "uppercase",
                                      letterSpacing: "1px",
                                    }}
                                  >
                                    Total Biaya Bahan Medis{" "}
                                    {getPersediaanBadgeText(sbuData.name)}
                                  </span>
                                  <div
                                    style={{
                                      fontSize: "18px",
                                      fontWeight: "bold",
                                      color: "#f5576c",
                                      marginTop: "4px",
                                    }}
                                  >
                                    {formatNominal(sbuData.total)}
                                  </div>
                                </div>
                                {index !== array.length - 1 && (
                                  <hr
                                    style={{
                                      margin: "16px 0",
                                      borderColor: "#e0e0e0",
                                    }}
                                  />
                                )}
                              </div>
                            ),
                          )}
                        </>
                      ) : (
                        <div className="text-center py-5 text-muted">
                          <Icons.Inbox size={48} className="mb-3" />
                          <p>Tidak ada data bahan medis</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
}
